import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/auth.store';
import { AnimatedNumber } from '../../components/ui/AnimatedNumber';
import { Odometer } from '../../components/ui/Odometer';
import { Icon } from '../../components/ui/Icon';
import { fmtEur, fmtDate, fmtDateShort } from '../../lib/utils';
import { canReadAdmin, canWriteAdmin } from '../admin/adminApi';
import { useAdminActions } from '../admin/useAdminActions';
import { ADMIN_PATH, adminLinks } from '../../lib/adminRoutes';
import { situazioneQuery, tempoInParole, type Problema, type Situazione } from '../admin/situazione/situazioneApi';
import { ActionStream } from './ActionStream';
import { useActionStream } from './useActionStream';
import { OggiMobile } from './OggiMobile';
import { useIsMobile } from '../../hooks/useIsMobile';
import {
  oggiQuery, FONTE_LABEL, NATURA_LABEL,
  type Natura, type OggiRigaFattura, type OggiRigaPagamento, type OggiSummary,
} from './oggiApi';
import './soldi.css';
import '../admin/situazione/situazione.css';

/**
 * Oggi — la home.
 *
 * In cima, per chi ha l'Amministrazione, "Come stiamo con i soldi": la frase
 * e tre numeri chiave presi dalla Situazione (stessa chiamata, stessa cache),
 * ognuno con il rimando alla pagina che lo spiega, e SOLO i problemi gravi —
 * mese previsto sotto zero, tasse entro 7 giorni, aggiornamenti falliti,
 * fatture in ritardo da più di due mesi. Lorenzo, 24/9: "una situazione più
 * riassuntiva e intuitiva con i rimandi ed eventuali problemi gravi". Le tre
 * colonne dei mesi e il "Da pagare in tutto" non ci sono più: il conto dei
 * mesi sta nella Situazione (SPEC amministrazione-v2, R2).
 *
 * Sotto, invariati: il lavoro in mano e in vendita, le fatture da incassare,
 * i pagamenti in arrivo e le cose da fare.
 *
 * Nessun conto fatto qui: i numeri arrivano da GET /admin/situazione e
 * GET /admin/oggi (admin.situazione.ts, admin.oggi.ts).
 */

/**
 * Sul telefono la home è un'altra pagina (OggiMobile): stessa cache, altro
 * ordine e molte meno cose. Sul computer resta questa, identica.
 */
export function OggiPage() {
  const isMobile = useIsMobile();
  return isMobile ? <div className="page"><OggiMobile /></div> : <OggiDesktop />;
}

function OggiDesktop() {
  const user = useAuthStore((s) => s.user);
  const canAdmin = canReadAdmin(user);
  const { counts } = useActionStream();

  const { data, isLoading, isError } = useQuery({ ...oggiQuery(), enabled: canAdmin, retry: 1 });
  const { data: sit, isLoading: loadingSit } = useQuery({ ...situazioneQuery({ mesi: 3 }), enabled: canAdmin, retry: 1 });

  const oggi = data?.today ?? sit?.oggi ?? new Date().toISOString().slice(0, 10);
  const dataLunga = new Date(`${oggi}T00:00:00.000Z`).toLocaleDateString('it-IT', {
    weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC',
  });

  return (
    <div className="page stagger">
      <div className="page-ed-hd">
        <h1 className="ed-title">Ciao, <strong>{user?.firstName}</strong>.</h1>
        <div className="ed-sub flex items-baseline gap-2.5 flex-wrap">
          <span>{dataLunga.charAt(0).toUpperCase() + dataLunga.slice(1)}</span>
          {counts.critical > 0 && (
            <>
              <span className="mute-2">·</span>
              <span className="text-alert font-semibold">
                {counts.critical} {counts.critical === 1 ? 'cosa da fare subito' : 'cose da fare subito'}
              </span>
            </>
          )}
        </div>
      </div>

      {!canAdmin ? (
        <NoSoldi />
      ) : (
        <>
          {/* Come stiamo con i soldi: frase, tre numeri, problemi gravi */}
          {sit ? <SoldiInBreve s={sit} /> : loadingSit ? <Scheletro /> : (
            <div className="card"><div className="card-bd text-[13px] mute">
              La Situazione non risponde: i numeri arrivano appena l'API è su. Le cose da fare restano qui sotto.
            </div></div>
          )}

          {isError ? null : !data ? (isLoading ? <ScheletroLista /> : null) : (
            <>
              {/* Lavoro in mano e lavoro in vendita, più la prova dei conti */}
              <div className="ed-divider" />
              <Lavoro d={data} />

              {/* Fatture da incassare e pagamenti in arrivo, affiancati */}
              <div className="ed-divider" />
              <div className="adm-bento">
                <Fatture d={data} />
                <DaPagare d={data} />
              </div>
            </>
          )}
        </>
      )}

      {/* Le cose da fare: restano, ma sotto i soldi */}
      <div className="ed-divider" />
      <div className="data-hd">
        <span className="t-micro">Cose da fare</span>
        <span className="mute t-small mono">{counts.critical + counts.today + counts.week} da gestire</span>
      </div>
      <div className="card">
        <div className="card-bd card-bd--tight"><ActionStream /></div>
      </div>
    </div>
  );
}

// ─── Come stiamo con i soldi ────────────────────────────────────────────────

/**
 * La Situazione in breve. Tre numeri, e ognuno porta dove si spiega:
 *   Soldi disponibili oggi               → Banca
 *   Saldo previsto a fine {mese più basso} → Situazione (i prossimi mesi)
 *   Pagamenti scaduti non ancora fatti   → Situazione (la fascia con la "?")
 * Sotto, solo se ci sono, i problemi gravi.
 */
function SoldiInBreve({ s }: { s: Situazione }) {
  const navigate = useNavigate();
  const stretto = s.previsione.mesi.find((m) => m.mese === s.previsione.piuStretto) ?? null;
  const go = (to: string) => () => navigate(to);
  const onKey = (to: string) => (e: React.KeyboardEvent) => { if (e.key === 'Enter') navigate(to); };
  return (
    <>
      <div className="data-hd">
        <span className="t-micro">Come stiamo con i soldi</span>
        <Link to={ADMIN_PATH.situazione} className="btn btn-sm btn-ghost">Situazione <Icon name="chevronR" size={12} /></Link>
      </div>
      <p className="sit-frase">{s.frase}</p>
      <div className="stat-band sit-band">
        <div className="st-click" role="button" tabIndex={0} title="Conti in banca più i contanti: i singoli conti sono in Banca" onClick={go(ADMIN_PATH.banca)} onKeyDown={onKey(ADMIN_PATH.banca)}>
          <div className="st-label">Soldi disponibili oggi</div>
          <div className="st-value"><span className="u">€</span><Odometer value={s.disponibili.totale} format={fmtEur} /></div>
          <div className="st-delta">{s.disponibili.bancaAggiornataTesto}</div>
        </div>
        {stretto && (
          <div className="st-click" role="button" tabIndex={0} title="Il mese con il saldo di fine mese più basso: il dettaglio è nella Situazione" onClick={go(ADMIN_PATH.situazione)} onKeyDown={onKey(ADMIN_PATH.situazione)}>
            <div className="st-label">Saldo previsto a fine {stretto.nome.toLowerCase()}</div>
            <div className={`st-value${stretto.saldoFine < 0 ? ' text-alert' : ''}`}>
              {stretto.saldoFine < 0 && '−'}<span className="u">€</span><AnimatedNumber value={Math.abs(stretto.saldoFine)} format={fmtEur} />
            </div>
            <div className="st-delta">il più basso dei prossimi {s.previsione.mesi.length} mesi</div>
          </div>
        )}
        <div className="st-click" role="button" tabIndex={0} title="Quello che risulta a sistema: il dettaglio è nella Situazione" onClick={go(ADMIN_PATH.situazione)} onKeyDown={onKey(ADMIN_PATH.situazione)}>
          <div className="st-label">Pagamenti scaduti non ancora fatti</div>
          <div className="st-value"><span className="u">€</span><AnimatedNumber value={s.scaduti.totale} format={fmtEur} /></div>
          <div className="st-delta">{s.scaduti.totale > 0 ? s.scaduti.descrizione : 'niente di scaduto'}</div>
        </div>
      </div>
      {s.problemi.length > 0 && <ProblemiGravi problemi={s.problemi} />}
    </>
  );
}

const ICONA_PROBLEMA: Record<Problema['tipo'], 'chart' | 'calendarClock' | 'refresh' | 'invoice'> = {
  MESE_SOTTO_ZERO: 'chart',
  TASSE_ENTRO_7: 'calendarClock',
  SYNC: 'refresh',
  FATTURE_OLTRE_60: 'invoice',
};

/** Il bottone dice dove porta: "Apri" da solo non lo diceva. */
const APRI_PROBLEMA: Record<Problema['tipo'], string> = {
  MESE_SOTTO_ZERO: 'Apri la Situazione',
  TASSE_ENTRO_7: 'Apri Fisco',
  SYNC: 'Apri la Situazione',
  FATTURE_OLTRE_60: 'Apri Fatture',
};

/** Solo le cose gravi, ognuna con il suo rimando. L'aggiornamento fallito si riprova da qui. */
function ProblemiGravi({ problemi }: { problemi: Problema[] }) {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const canWrite = canWriteAdmin(user);
  const { syncQonto, syncFic } = useAdminActions();
  return (
    <div className="card og-problemi">
      <div className="card-hd">
        <div className="t-h3">Problemi da guardare subito</div>
      </div>
      <div className="card-bd card-bd--tight">
        {problemi.map((p, i) => {
          const sync = p.provider === 'QONTO' ? syncQonto : syncFic;
          return (
            <div key={`${p.tipo}-${i}`} className="adm-line">
              <div className="icon-tile sm" style={{ ['--c' as string]: 'var(--alert)' }}><Icon name={ICONA_PROBLEMA[p.tipo]} size={14} /></div>
              <div className="min-w-0 flex-1">
                <div className="font-semibold">{p.testo}</div>
                {p.dettaglio && <div className="text-[12px] mute">{p.dettaglio}</div>}
              </div>
              {p.tipo === 'SYNC' && canWrite ? (
                <button className="btn btn-sm shrink-0" disabled={sync.isPending} onClick={() => sync.mutate()}>
                  <Icon name="refresh" size={10} /> {sync.isPending ? 'Aggiornamento…' : 'Riprova'}
                </button>
              ) : (
                <button className="btn btn-sm btn-ghost shrink-0" onClick={() => navigate(p.link)}>{APRI_PROBLEMA[p.tipo]} <Icon name="chevronR" size={10} /></button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Pezzi ──────────────────────────────────────────────────────────────────

/** "fatto", "certo", "previsione": la natura del numero accanto al numero. */
function Tag({ natura }: { natura: Natura }) {
  return <em className="og-tag">{NATURA_LABEL[natura]}</em>;
}

/**
 * La data di una scadenza in poco spazio.
 *
 * "16 mar" va bene finché si resta nell'anno in corso. Fuori dall'anno
 * corrente l'anno si scrive, altrimenti le date di anni diversi ordinate per
 * data vera sembrano messe a caso.
 */
function scadenza(iso: string | null, annoOggi: number): string {
  if (!iso) return '—';
  const anno = Number(iso.slice(0, 4));
  if (!Number.isFinite(anno)) return '—';
  if (anno === annoOggi) return fmtDateShort(iso);
  // iso è "YYYY-MM-DD": si prendono i pezzi per posizione, senza destrutturare
  // (con noUncheckedIndexedAccess gli elementi di split() sono possibly undefined)
  return `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(2, 4)}`;
}

/**
 * Due cose diverse che non vanno sommate alla leggera: il lavoro che abbiamo
 * in mano (contratti, progetti, canoni firmati) e quello che stiamo ancora
 * vendendo (trattative aperte). Sotto, in chiaro, quanto è stato tolto per non
 * contare lo stesso euro due volte.
 */
function Lavoro({ d }: { d: OggiSummary }) {
  const navigate = useNavigate();
  const [proof, setProof] = useState(false);
  const l = d.lavoro;
  const c = d.controllo;
  return (
    <>
      <div className="data-hd">
        <span className="t-micro">Lavoro in mano e lavoro in vendita</span>
        <button type="button" className="btn btn-sm btn-ghost" onClick={() => setProof((v) => !v)}>
          {proof ? 'Nascondi la prova dei doppioni' : 'Come non contiamo due volte'} <Icon name={proof ? 'chevronU' : 'chevronD'} size={12} />
        </button>
      </div>

      <div className="stat-band og-band4">
        <div>
          <div className="st-label">Lavoro in mano, in tutto</div>
          <div className="st-value"><span className="u">€</span><AnimatedNumber value={l.inMano.totale} format={fmtEur} /></div>
          <div className="st-delta">progetti, trattative vinte e canoni firmati</div>
        </div>
        <div className="st-click" role="button" tabIndex={0} onClick={() => navigate('/progetti')} onKeyDown={(ev) => { if (ev.key === 'Enter') navigate('/progetti'); }}>
          <div className="st-label">Progetti da consegnare</div>
          <div className="st-value"><span className="u">€</span><AnimatedNumber value={l.inMano.progetti.valore} format={fmtEur} /></div>
          <div className="st-delta">
            {/* "Nessun progetto aperto" sarebbe falso quando i progetti ci sono
                ma si fatturano tutti a canone: l'elenco Progetti ne mostrerebbe
                cinque e questa riga direbbe zero. */}
            {l.inMano.progetti.count === 0
              ? l.inMano.progettiACanone.count > 0
                ? 'tutti fatturati a canone, qui sotto'
                : 'nessun progetto aperto'
              : `${l.inMano.progetti.count} progett${l.inMano.progetti.count === 1 ? 'o' : 'i'} · residuo da fatturare`}
          </div>
        </div>
        <div>
          <div className="st-label">Canoni dei clienti, {l.inMano.ricorrente.mesi} mesi</div>
          <div className="st-value"><span className="u">€</span><AnimatedNumber value={l.inMano.ricorrente.valore} format={fmtEur} /></div>
          <div className="st-delta">{l.inMano.ricorrente.count} incassi fino al {fmtDate(l.inMano.ricorrente.fino)}</div>
        </div>
        <div className="st-click" role="button" tabIndex={0} onClick={() => navigate('/trattative')} onKeyDown={(ev) => { if (ev.key === 'Enter') navigate('/trattative'); }}>
          <div className="st-label">Trattative in corso</div>
          <div className="st-value"><span className="u">€</span><AnimatedNumber value={l.inVendita.valore} format={fmtEur} /></div>
          <div className="st-delta">{l.inVendita.count} aperte · pesato sulla probabilità € {fmtEur(l.inVendita.pesato)}</div>
        </div>
      </div>

      {l.inMano.progettiACanone.count > 0 && (
        <div className="adm-note">
          <strong>€ {fmtEur(l.inMano.progettiACanone.valore)}</strong>
          <span>
            di progetti ({l.inMano.progettiACanone.count}) non sono in questi totali: si fatturano a rate,
            e le rate sono già contate fra i canoni dei clienti qui sopra.
          </span>
        </div>
      )}

      {/* Il punto cieco, scritto insieme agli altri e non nascosto. La regola
          "vince la fattura" confronta cliente, mese e importo: sui canoni
          aggregati (GoDeal, Stripe, hosting annuali) il cliente non c'è, e il
          confronto non parte. Se uno di quei clienti ha una fattura aperta, il
          suo pezzo è contato due volte. Va guardato a mano. */}
      {l.inMano.ricorrente.aggregati.valore > 0 && (
        <div className="adm-note">
          <strong>€ {fmtEur(l.inMano.ricorrente.aggregati.valore)}</strong>
          <span>
            dei canoni sono voci aggregate senza cliente ({l.inMano.ricorrente.aggregati.count} incassi):
            su queste il controllo dei doppioni non può scattare, perché confronta il cliente.
            Se un abbonamento lì dentro ha anche una fattura aperta, quel pezzo è contato due volte.
          </span>
        </div>
      )}

      {l.inVendita.rinnovi.count > 0 && (
        <div className="adm-note">
          <strong>€ {fmtEur(l.inVendita.rinnovi.valore)}</strong>
          <span>
            di rinnovi ({l.inVendita.rinnovi.count}) non sono in questi totali: il canone che rinnovano
            è già contato fra i canoni dei clienti qui sopra.
          </span>
        </div>
      )}

      {proof && <Prova c={c} />}
    </>
  );
}

/**
 * La prova, in euro. Le cinque voci in alto sono disgiunte per costruzione;
 * sotto c'è quanto è stato tolto per renderle tali, con la regola che lo dice e
 * dove è scritta. Sommando alla cieca il totale sarebbe `totaleLordo`, cioè
 * `differenza` euro che non esistono.
 */
function Prova({ c }: { c: OggiSummary['controllo'] }) {
  return (
    <div className="adm-fold">
      <div className="t-micro" style={{ marginBottom: 12 }}>Il valore del lavoro, voce per voce</div>
      <table className="og-proof">
        <tbody>
          {c.voci.map((v) => (
            <tr key={v.chiave}>
              <td>{v.etichetta}<em className="og-tag">{NATURA_LABEL[v.natura]}</em></td>
              <td className="n">€ {fmtEur(v.valore)}</td>
            </tr>
          ))}
          <tr className="is-total">
            <td>Totale, senza doppioni</td>
            <td className="n">€ {fmtEur(c.totaleNetto)}</td>
          </tr>
        </tbody>
      </table>

      <div className="t-micro" style={{ margin: '22px 0 12px' }}>Tolto perché sarebbe stato contato due volte</div>
      <table className="og-proof">
        <tbody>
          {c.scartato.map((s) => (
            <tr key={s.chiave}>
              <td>
                {s.etichetta} {s.count > 0 && <span className="mute-2">({s.count})</span>}
                <span className="og-why">{s.regola}</span>
              </td>
              <td className="n">− € {fmtEur(s.valore)}</td>
            </tr>
          ))}
          <tr className="is-total">
            <td>
              Sommando alla cieca farebbe € {fmtEur(c.totaleLordo)}
              <span className="og-why">
                {c.quadra
                  ? `€ ${fmtEur(c.totaleLordo)} meno € ${fmtEur(c.totaleNetto)} fa esattamente € ${fmtEur(c.differenza)}: il conto degli scarti torna.`
                  : 'Attenzione: la differenza non coincide con la somma degli scarti.'}
              </span>
            </td>
            <td className={`n${c.quadra ? '' : ' text-alert'}`}>€ {fmtEur(c.differenza)} di troppo</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

/**
 * Le fatture: prima quelle in ritardo, poi quelle che devono ancora scadere.
 * In quest'ordine, sempre. Il totale da incassare ha la sua casa nella
 * Situazione (R2): qui si contano le fatture e si elencano le righe.
 */
function Fatture({ d }: { d: OggiSummary }) {
  const navigate = useNavigate();
  const f = d.fatture;
  const annoOggi = Number(d.today.slice(0, 4));
  const apri = (r: OggiRigaFattura) => navigate(adminLinks.fattura(r.numero));
  return (
    <div className="card oggi-fill">
      <div className="card-hd">
        <div>
          <div className="t-h3">Fatture da incassare</div>
          <div className="mute t-small">
            {f.scadute.count > 0
              ? <>{f.scadute.count} {f.scadute.count === 1 ? 'rata in ritardo' : 'rate in ritardo'} su {f.scadute.count + f.prossime.count} da incassare</>
              : <>nessuna in ritardo · {f.prossime.count} {f.prossime.count === 1 ? 'rata da incassare' : 'rate da incassare'}</>}
          </div>
        </div>
        <Link to={ADMIN_PATH.fatture} className="btn btn-sm btn-ghost">Fatture <Icon name="chevronR" size={12} /></Link>
      </div>
      <div className="card-bd card-bd--tight">
        {f.scadute.count > 0 && (
          <>
            <div className="t-micro text-alert" style={{ padding: '6px 0 2px' }}>Fatture in ritardo</div>
            {f.scadute.voci.map((r) => <RigaFatturaView key={r.paymentId} r={r} anno={annoOggi} scaduta onClick={() => apri(r)} />)}
          </>
        )}
        <div className="t-micro" style={{ padding: f.scadute.count > 0 ? '14px 0 2px' : '6px 0 2px' }}>
          Fatture che devono ancora scadere
        </div>
        {f.prossime.voci.length === 0
          ? <div className="mute text-[13px] py-3">Nessuna rata in arrivo.</div>
          : f.prossime.voci.map((r) => <RigaFatturaView key={r.paymentId} r={r} anno={annoOggi} onClick={() => apri(r)} />)}
      </div>
    </div>
  );
}

function RigaFatturaView({ r, anno, scaduta, onClick }: { r: OggiRigaFattura; anno: number; scaduta?: boolean; onClick: () => void }) {
  return (
    <div className="adm-line cursor-pointer" role="button" tabIndex={0} onClick={onClick} onKeyDown={(e) => { if (e.key === 'Enter') onClick(); }}>
      <div className={`og-when mono${scaduta ? ' text-alert' : ''}`} title={r.dueDate ? `Scadenza: ${fmtDate(r.dueDate)}` : undefined}>{scadenza(r.dueDate, anno)}</div>
      <div className="min-w-0 flex-1">
        <div className="truncate">{r.cliente}</div>
        <div className="text-[12px] mute">
          {r.numero ?? 'senza numero'}
          {r.rata && ` · rata ${r.rata.seq} di ${r.rata.su}`}
          {scaduta && r.giorniDiRitardo > 0 && ` · in ritardo ${tempoInParole(-r.giorniDiRitardo)}`}
        </div>
      </div>
      <div className={`og-amount${scaduta ? ' text-alert' : ''}`}>€ {fmtEur(r.importo)}</div>
    </div>
  );
}

/**
 * I pagamenti da fare da oggi alla fine del mese prossimo. Lo scaduto non c'è
 * (R4): sta in "Pagamenti scaduti non ancora fatti", qui sopra. Le spese fisse
 * e gli stipendi sono proiezioni, e la cifra lo dice.
 */
function DaPagare({ d }: { d: OggiSummary }) {
  const p = d.daPagare;
  const fino = new Date(`${p.fino}T00:00:00.000Z`).toLocaleDateString('it-IT', { month: 'long', timeZone: 'UTC' });
  return (
    <div className="card oggi-fill">
      <div className="card-hd">
        <div>
          <div className="t-h3">Pagamenti da fare fino a fine {fino}</div>
          <div className="mute t-small">€ {fmtEur(p.totale)} in tutto, da oggi al {fmtDate(p.fino)}</div>
        </div>
        <Link to={ADMIN_PATH.spese} className="btn btn-sm btn-ghost">Spese <Icon name="chevronR" size={12} /></Link>
      </div>
      <div className="card-bd card-bd--tight">
        <RigaTotale k="Fatture dei fornitori" v={p.fornitori} natura="CERTO" />
        <RigaTotale k="Tasse" v={p.tasse} natura="CERTO" />
        {/* Dentro le tasse c'è anche quello che NON è un F24 del
            commercialista: l'IVA calcolata da noi, l'F24 stimato sulla media.
            Si scrive sotto, accanto alla cifra, non nelle note. */}
        {p.tasseStima > 0 && (
          <div className="og-row"><span className="k">di cui tasse stimate da noi<Tag natura="PREVISIONE" /></span><span className="n">€ {fmtEur(p.tasseStima)}</span></div>
        )}
        <RigaTotale k="Spese fisse" v={p.speseFisse} natura="PREVISIONE" />
        <RigaTotale k="Stipendi e TFR" v={p.stipendi} natura="PREVISIONE" />
        {p.manuali > 0 && <RigaTotale k="Uscite scritte a mano" v={p.manuali} natura="PREVISIONE" />}

        <div className="t-micro" style={{ padding: '16px 0 2px' }}>I prossimi pagamenti</div>
        {p.voci.length === 0
          ? <div className="mute text-[13px] py-3">Non c'è niente da pagare fino a fine {fino}.</div>
          : p.voci.map((v) => <RigaPagamentoView key={`${v.fonte}-${v.id}`} v={v} anno={Number(d.today.slice(0, 4))} />)}
      </div>
    </div>
  );
}

function RigaTotale({ k, v, natura }: { k: string; v: number; natura: Natura }) {
  return (
    <div className={`og-row${v === 0 ? ' is-zero' : ''}`}>
      <span className="k">{k}<Tag natura={natura} /></span>
      <span className="n">€ {fmtEur(v)}</span>
    </div>
  );
}

function RigaPagamentoView({ v, anno }: { v: OggiRigaPagamento; anno: number }) {
  return (
    <div className="adm-line">
      <div className="og-when mono" title={v.dueDate ? `Scadenza: ${fmtDate(v.dueDate)}` : undefined}>{scadenza(v.dueDate, anno)}</div>
      <div className="min-w-0 flex-1">
        <div className="truncate">{v.controparte}</div>
        <div className="text-[12px] mute truncate">
          {FONTE_LABEL[v.fonte]}
          {v.descrizione && v.descrizione !== v.controparte && ` · ${v.descrizione}`}
          {!v.documento && v.fonte !== 'TASSE' && ' · previsione'}
        </div>
      </div>
      <div className="og-amount">€ {fmtEur(v.importo)}</div>
    </div>
  );
}

function NoSoldi() {
  return (
    <div className="card">
      <div className="card-bd text-[13px] mute">
        I numeri di cassa sono riservati a chi ha l'Amministrazione. Qui sotto restano le cose da fare.
      </div>
    </div>
  );
}

function Scheletro() {
  return (
    <div className="stat-band">
      {[0, 1, 2].map((i) => (
        <div key={i}><span className="skeleton sk-sm" style={{ maxWidth: 110 }} /><span className="skeleton sk-lg" style={{ maxWidth: 160 }} /></div>
      ))}
    </div>
  );
}

function ScheletroLista() {
  return (
    <div className="adm-bento" style={{ marginTop: 24 }}>
      {[0, 1].map((i) => (
        <div key={i} className="card"><div className="card-bd skeleton-col">
          <span className="skeleton sk-block" /><span className="skeleton sk-block" /><span className="skeleton sk-block" />
        </div></div>
      ))}
    </div>
  );
}
